import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from './ui';

/**
 * The one legal notice every visitor sees before anything else.
 *
 * MySCAW sets a single essential session cookie, so there is nothing to
 * consent to and no consent gate - INFORMATION ONLY, the same way the privacy
 * policy on the landing footer is. "Compris" stores the dismissal and the
 * banner never comes back; on a phone it floats above the fixed tab bar
 * (56px + safe-area, TabBar.tsx), which is why it sits at `bottom-20` there
 * and drops to the corner from `sm` up where no tab bar exists.
 *
 * Storage is wrapped because the same Node-22 local operation that breaks the
 * i18n bootstrap (test/setup.ts) would otherwise take the header down too; a
 * banner that fails to remember "you told me already" is worse than none.
 */
const STORAGE_KEY = 'myscaw.cookie-notice';
const DISMISSED = '1';

function alreadyDismissed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === DISMISSED;
  } catch {
    return false;
  }
}

export function CookieBanner() {
  const [dismissed, setDismissed] = useState(alreadyDismissed);
  const { t } = useTranslation();

  if (dismissed) {
    return null;
  }

  function accept(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, DISMISSED);
    } catch {
      // No usable storage; the banner is informational, so forgetting is fine.
    }
    setDismissed(true);
  }

  return (
    <div
      role="region"
      aria-label={t('cookie.noticeLabel')}
      className="fixed inset-x-4 bottom-20 z-50 sm:inset-x-auto sm:bottom-6 sm:left-6 sm:right-6 mx-auto max-w-md rounded-2xl border border-outline-variant bg-surface-container-lowest shadow-card-hover p-4 flex flex-col gap-3"
    >
      <p className="font-body-md text-on-surface">{t('cookie.body')}</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
        <Link
          to="/privacy"
          className="inline-flex items-center min-h-[44px] sm:min-h-0 font-title-md text-primary hover:text-primary-container transition-colors duration-chip"
        >
          {t('cookie.privacy')}
        </Link>
        <Button type="button" className="w-full sm:w-auto" onClick={accept}>
          {t('cookie.accept')}
        </Button>
      </div>
    </div>
  );
}
